import React, {
  useRef,
  useState,
} from 'react';
import Pill from 'core/components/Pill';
import {
  TagDTO,
} from 'dtos/tagDTO';
import ToolTip from 'core/components/Tooltip';
import Skeleton from 'core/components/Skeleton';
import Popover, {
  Props as PopoverProps,
} from 'core/components/Popover';
import {
  useGetClaimTags,
  useUpdateClaimWiseTags,
} from 'pages/Dashboard/pages/Billing/hooks';
import map from 'lodash/map';
import compact from 'lodash/compact';
import clsx from 'clsx';
import Button from 'pages/Dashboard/components/Button';
import AddIcon from '@mui/icons-material/Add';
import {
  colorClasses,
} from 'pages/Dashboard/pages/Encounters/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useUpsertTag,
} from 'views/EMR/Settings/hooks';
import {
  getApiTagGetTags,
} from 'endpoints';
import PopulateLookup from 'core/components/Lookup';
import {
  getColorForString,
} from 'utils/colors';

type Props = {
  claimId: number;
  className?: string;
  popoverProps?: Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>;
  encounterId?: number;
};

export default function ClaimTags({
  claimId,
  className,
  popoverProps = {},
  encounterId,
}: Props) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    data: tags = [],
    isLoading,
    refetch,
  } = useGetClaimTags(claimId);

  const {
    mutateAsync: updateTags,
    isPending: isUpdating,
  } = useUpdateClaimWiseTags();

  const disabled = isLoading || isUpdating;

  const saveTags = async (tags: TagDTO[]) => {
    setContainer(null);

    await updateTags({
      claimId,
      tagIds: compact(map(tags, 'tagId')),
    });
    await refetch();

    billingEventChannel.emit('onClaimTagsUpdated', claimId);
  };

  const onRemoveTag = async (index: number) => {
    const tagList = [
      ...tags.slice(0, index),
      ...tags.slice(index + 1),
    ];
    await saveTags(tagList);
  };

  const { mutateAsync: upsertTag } = useUpsertTag();

  return (
    <>
      <div
        className={clsx('flex flex-wrap gap-2', className)}
        ref={containerRef}
      >
        {isLoading
          ? (
            <>
              <Skeleton className="!w-20 !h-5" />
              <Skeleton className="!w-16 !h-5" />
            </>
          )
          : tags.map(({ tagId, name }, index) => (
            <Pill
              key={tagId}
              id={name ?? ''}
              label={name ?? ''}
              disabled={disabled}
              onRemove={() => { onRemoveTag(index); }}
              classes={{
                root: '!h-5',
                label: 'font-semibold',
              }}
              defaultColor={getColorForString(name ?? '', colorClasses)}
            />
          ))}
        {isUpdating
          ? <Spinner />
          : (
            <ToolTip title="Add Tag">
              <Button
                className={clsx('!border-dashed !rounded-full !py-0 !px-1 !w-5 !h-5 !min-w-[initial] !flex !items-center', {
                  '!border-blue-500': !isUpdating && !isLoading,
                  '!border-gray-300': isUpdating || isLoading,
                })}
                color="primary"
                type="button"
                variant="outlined"
                disabled={isLoading || isUpdating}
                onClick={() => { setContainer(containerRef?.current); }}
              >
                <AddIcon className="!w-4 !h-4" />
              </Button>
            </ToolTip>
          )}
      </div>
      <Popover
        anchorEl={container}
        onClose={() => { setContainer(null); }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        classes={{ paper: '!w-64' }}
        {...popoverProps}
      >
        <PopulateLookup<TagDTO>
          multiple
          value={tags}
          type="tag"
          onChange={(newValue) => {
            saveTags(newValue as TagDTO[]);
          }}
          placeholder="Search tags"
          readonly={disabled}
          getOptions={getApiTagGetTags}
          minSearchSize={0}
          getUniqueId={(value) => value?.name?.toLowerCase() ?? ''}
          isOptionEqualToValue={(
            option,
            value,
          ) => option?.name?.toLowerCase() === (value as TagDTO)?.name?.toLowerCase()}
          getLabel={(value) => value?.name ?? ''}
          onAddNew={async (value) => {
            const newTag = await upsertTag({ name: value });
            return newTag;
          }}
          classes={{ listbox: '!max-h-[180px]' }}
          renderOptionRow={(option) => (
            <div className="flex flex-col items-start justify-start w-full text-left gap-2">
              <div className="flex flex-row items-center justify-start w-full gap-1 ">
                <span className="font-semibold">
                  {option?.name ?? '--'}
                </span>
              </div>
            </div>
          )}
        />
      </Popover>
    </>
  );
}
